import { Button, Grid, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/system'
import React, { ChangeEvent, useEffect, useState } from 'react'
import CustomTextField from '../../components/common/CustomTextField'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import TableButtonDelete from '../../components/table/TableButtonDelete'
import { InvoiceIssuerBriefDto, InvoiceIssuerDto } from '../../services/api/sharedTypes/Invoice'
import UploadRoundedIcon from '@mui/icons-material/UploadRounded'
import { InvoiceManager } from '../../services/api/InvoiceManager'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import RemoveImageDialog from './removeImageDialog'
import { setSaveState } from '../../services/redux/savestate/savestate-actions'
import CustomContainer from '../../components/common/CustomContainer'
import { UpdateInvoiceIssuerResponse } from '../../services/api/collections/invoice-Issuer/updateInvoiceIssuer/updateInvoiceIssuer_200'

interface InvoiceIssuerProps {
    id: string
    index: number
    isLoading: boolean
    onDelete: (id: string) => void
    onUpdate: (id: string, invoiceIssuer: InvoiceIssuerBriefDto) => Promise<UpdateInvoiceIssuerResponse | undefined>
    uploadInvoiceIssuerImage: (id: string, file?: File) => Promise<UpdateInvoiceIssuerResponse | undefined>
}

const oneYearFromNow = new Date().setFullYear(new Date().getFullYear() - 1)

const months = [
    'leden',
    'únor',
    'březen',
    'duben',
    'květen',
    'červen',
    'červenec',
    'srpen',
    'září',
    'říjen',
    'listopad',
    'prosinec',
]

const InvoiceIssuer: React.FC<InvoiceIssuerProps> = ({
    id,
    index,
    isLoading,
    onDelete,
    onUpdate,
    uploadInvoiceIssuerImage,
}: InvoiceIssuerProps) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [invoiceIssuer, setInvoiceIssuer] = useState<InvoiceIssuerDto>()
    const [oldInvoiceIssuer, setOldInvoiceIssuer] = useState<InvoiceIssuerDto>()
    const [loadingPageData, setLoadingPageData] = useState<boolean>(isLoading)

    const canGoBack =
        invoiceIssuer?.address === oldInvoiceIssuer?.address &&
        invoiceIssuer?.name === oldInvoiceIssuer?.name &&
        invoiceIssuer?.valueAddedTaxIdentificationNumber === oldInvoiceIssuer?.valueAddedTaxIdentificationNumber &&
        invoiceIssuer?.companyRegistrationNumber === oldInvoiceIssuer?.companyRegistrationNumber &&
        invoiceIssuer?.bankAccount === oldInvoiceIssuer?.bankAccount &&
        invoiceIssuer?.iban === oldInvoiceIssuer?.iban &&
        invoiceIssuer?.swift === oldInvoiceIssuer?.swift &&
        invoiceIssuer?.paymentDays === oldInvoiceIssuer?.paymentDays &&
        invoiceIssuer?.invoicedBy === oldInvoiceIssuer?.invoicedBy

    const fetchData = async () => {
        setLoadingPageData(true)
        try {
            const invoicesRes = await InvoiceManager.getManager().getInvoiceIssuerDetailAsync(id)
            if (invoicesRes) {
                setInvoiceIssuer(invoicesRes)
                setOldInvoiceIssuer(invoicesRes)
            }
        } catch (error) {
            setLoadingPageData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }

        setLoadingPageData(false)
    }
    useEffect(() => {
        fetchData()
    }, [setLoadingPageData])

    useEffect(() => {
        dispatch(setSaveState(canGoBack))
    }, [canGoBack, dispatch])

    const onFileChanged = async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || !invoiceIssuer) {
            return
        }
        const newInvoiceIssuer = await uploadInvoiceIssuerImage(invoiceIssuer.id, event.target.files[0])
        setInvoiceIssuer(newInvoiceIssuer)
    }

    const onFileRemoved = async () => {
        if (!invoiceIssuer) {
            return
        }
        const newInvoiceIssuer = await uploadInvoiceIssuerImage(invoiceIssuer.id)
        setInvoiceIssuer(newInvoiceIssuer)
    }

    if (loadingPageData) return <ScreenLoadingPlaceholder />
    if (!invoiceIssuer) return null

    return (
        <CustomContainer bgColor={theme.palette.background.default}>
            <Stack justifyContent='space-between' direction='row'>
                <Typography variant='body1'>Fakturace {index}</Typography>
                <Stack direction='row' spacing={3}>
                    <Button
                        size='small'
                        variant='contained'
                        onClick={() => {
                            onUpdate(invoiceIssuer.id, invoiceIssuer)
                            dispatch(setSaveState(true))
                        }}
                    >
                        Uložit
                    </Button>
                    <TableButtonDelete
                        nameOfEntityInDialogMessage={invoiceIssuer.name}
                        onClick={() => onDelete(invoiceIssuer.id)}
                    />
                </Stack>
            </Stack>
            <Grid container spacing={3}>
                <Grid item xs={12} lg={5}>
                    <Grid container rowSpacing={3} columnSpacing={3}>
                        <Grid item xs={6}>
                            <Typography variant='body2' mb={2}>
                                Základní údaje
                            </Typography>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='Název'
                                value={invoiceIssuer.name ?? ''}
                                onChange={(e) => setInvoiceIssuer({ ...invoiceIssuer, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant='body2' mb={2} visibility='hidden'>
                                PLACEHOLDER
                            </Typography>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='Adresa'
                                value={invoiceIssuer.address ?? ''}
                                onChange={(e) => setInvoiceIssuer({ ...invoiceIssuer, address: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='IČ'
                                value={invoiceIssuer.companyRegistrationNumber ?? ''}
                                onChange={(e) =>
                                    setInvoiceIssuer({ ...invoiceIssuer, companyRegistrationNumber: e.target.value })
                                }
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='DIČ'
                                value={invoiceIssuer.valueAddedTaxIdentificationNumber ?? ''}
                                onChange={(e) =>
                                    setInvoiceIssuer({ ...invoiceIssuer, valueAddedTaxIdentificationNumber: e.target.value })
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} lg={5}>
                    <Grid container rowSpacing={3} columnSpacing={3}>
                        <Grid item xs={4}>
                            <Typography variant='body2' mb={2}>
                                Fakturace
                            </Typography>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='Bankovní účet'
                                value={invoiceIssuer.bankAccount ?? ''}
                                onChange={(e) => setInvoiceIssuer({ ...invoiceIssuer, bankAccount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant='body2' mb={2} visibility='hidden'>
                                PLACEHOLDER
                            </Typography>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='IBAN'
                                value={invoiceIssuer.iban ?? ''}
                                onChange={(e) => setInvoiceIssuer({ ...invoiceIssuer, iban: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant='body2' mb={2} visibility='hidden'>
                                PLACEHOLDER
                            </Typography>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='SWIFT'
                                value={invoiceIssuer.swift ?? ''}
                                onChange={(e) => setInvoiceIssuer({ ...invoiceIssuer, swift: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='Splatnost'
                                value={invoiceIssuer.paymentDays ?? ''}
                                onChange={(e) =>
                                    !isNaN(Number(e.target.value)) &&
                                    setInvoiceIssuer({ ...invoiceIssuer, paymentDays: Number(e.target.value) ?? 0 })
                                }
                            />
                        </Grid>
                        <Grid item xs={7}>
                            <CustomTextField
                                size='small'
                                fullWidth
                                label='Fakturuje'
                                value={invoiceIssuer.invoicedBy ?? ''}
                                onChange={(e) => setInvoiceIssuer({ ...invoiceIssuer, invoicedBy: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} lg={2}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Typography variant='body2'>Obrázek</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!invoiceIssuer.imageName ? (
                                <label htmlFor={'upload-file' + index}>
                                    <input
                                        accept='image/*'
                                        onChange={(e) => onFileChanged(e)}
                                        style={{ display: 'none' }}
                                        id={'upload-file' + index}
                                        name={'upload-file' + index}
                                        type='file'
                                    />
                                    <Button
                                        color='primary'
                                        variant='text'
                                        component='span'
                                        style={{ marginBottom: theme.spacing(3) }}
                                    >
                                        <UploadRoundedIcon />
                                        Nahrát obrázek
                                    </Button>
                                </label>
                            ) : (
                                <Stack>
                                    <Stack direction='row' maxWidth='10' overflow='auto' alignItems='center' mb={3}>
                                        <Typography
                                            style={{
                                                display: '-webkit-box',
                                                overflow: 'hidden',
                                                WebkitBoxOrient: 'vertical',
                                                WebkitLineClamp: 1,
                                            }}
                                        >
                                            {invoiceIssuer.imageName}
                                        </Typography>
                                        <RemoveImageDialog onRemove={onFileRemoved} />
                                    </Stack>
                                    <img
                                        src={`data:image/png;base64,${invoiceIssuer.imageData}`}
                                        alt={invoiceIssuer.imageName}
                                        style={{ maxWidth: '100px', maxHeight: '100px', position: 'absolute', marginTop: '30px' }}
                                    />
                                </Stack>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='body2' mb={2} visibility='hidden'>
                                PLACEHOLDER
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant='body1'>
                        Vystavené faktury za posledních 12 měsíců (od {months[Number(new Date(oneYearFromNow).getMonth())]}{' '}
                        {new Date(oneYearFromNow).getFullYear()}):{' '}
                        {new Intl.NumberFormat('cs-CS').format(Math.round(invoiceIssuer.invoicedLast12Months))} Kč
                    </Typography>
                </Grid>
            </Grid>
        </CustomContainer>
    )
}

export default InvoiceIssuer
